import { ReactNode, SyntheticEvent, useEffect, useState } from "react";
import { Clear } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  styled,
  TextField,
  Typography as T,
} from "@mui/material";
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";
import { SearchIcon } from "~/assets/icons";
import noop from "~/utils/noop";

const StyledMenuItem = styled("li")(({ theme }) => ({
  display: "flex",
  gap: 4,
  margin: 6,
  borderRadius: 4,
  "&:hover": {
    background: `${theme.palette.grey[50]}`,
  },
  "&.Mui-focus, &.Mui-focusVisible": {
    background: `${theme.palette.grey[100]} !important`,
  },
  svg: {
    fontSize: 16,
    color: theme.palette.primary.dark,
  },
}));

type SearchOption = {
  usePathPrefix: boolean;
};

interface ContextualSearchProps {
  loading: boolean;
  currentPathLabel?: ReactNode;
  submitCallback?: (params: { query: string; root: boolean }) => any;
}

/**
 * Reusable search component that updates related query string parameters on submit (enter)
 * @param loading - Status of search response
 * @param currentPathLabel - Used to show dropdown with options for user to decide search scope
 * @param submitCallback - Callback function to run after submission
 */
const ContextualSearch = ({
  loading,
  currentPathLabel,
  submitCallback = noop,
}: ContextualSearchProps) => {
  const [params, setParams] = useQueryParams({
    q: withDefault(StringParam, ""),
    page: NumberParam,
    root: BooleanParam,
  });
  const [search, setSearch] = useState(params.q);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    // Reset input on url change
    setSearch(params.q ?? "");
  }, [params.q]);

  const handleSubmit = (e: SyntheticEvent, value: SearchOption | string | null) => {
    if (search) {
      const newParams = {
        q: search,
        page: undefined,
      };
      // Set search in url and reset pagination
      setParams(newParams);
      if (typeof value === "object") {
        submitCallback({ query: search, root: !(value as SearchOption)?.usePathPrefix });
      }
    }
  };

  const clearSearch = () => {
    setParams({
      q: undefined,
      page: undefined,
    });
  };

  return (
    <Autocomplete
      freeSolo
      fullWidth
      blurOnSelect={!!currentPathLabel}
      disableClearable
      autoHighlight
      open={dropdownOpen}
      inputValue={search}
      onFocus={() => setDropdownOpen(true)}
      onBlur={() => setDropdownOpen(false)}
      onInputChange={(_, value, reason) => {
        if (reason !== "reset") {
          setSearch(value);
          if (!value) {
            clearSearch();
          }
        }
      }}
      size="small"
      forcePopupIcon={false}
      options={[{ usePathPrefix: true }, { usePathPrefix: false }]}
      filterOptions={(options) => options}
      onChange={handleSubmit}
      renderInput={(props) => (
        <TextField
          {...props}
          variant="outlined"
          placeholder="Search"
          sx={{
            fieldset: {
              borderColor: "rgba(0, 0, 0, 0.12)",
            },
          }}
          InputProps={{
            ...props.InputProps,
            startAdornment: (
              <Box display="flex" alignItems="center" justifyContent="center" pl={0.5}>
                <SearchIcon width={14} />
              </Box>
            ),
            endAdornment: (
              <Box display="flex" alignItems="center" gap={0.25}>
                {loading && (
                  <CircularProgress
                    variant="indeterminate"
                    disableShrink
                    thickness={4}
                    size={16}
                    sx={{
                      animationDuration: "550ms",
                    }}
                  />
                )}
                {search && (
                  <IconButton
                    edge="end"
                    size="small"
                    aria-label="Clear search"
                    onClick={(e) => {
                      // Prevent event bubbling to avoid weird focus behavior
                      e.stopPropagation();

                      setSearch("");
                      if (params.q) {
                        // If clearing previous query, remove query string
                        // params and restore previous directory view path
                        clearSearch();
                      }
                    }}
                    sx={{ fontSize: 20, p: 0.25 }}
                  >
                    <Clear fontSize="inherit" />
                  </IconButton>
                )}
              </Box>
            ),
          }}
        />
      )}
      renderOption={(optionProps, option) => {
        const searchDest = option.usePathPrefix ? (
          <>
            in{" "}
            <Box component="span" fontWeight={600}>
              {currentPathLabel}
            </Box>
          </>
        ) : (
          <Box component="span">everywhere</Box>
        );
        return (
          <StyledMenuItem {...optionProps} style={{ padding: 4 }}>
            <Box minWidth={12}>
              <SearchIcon />
            </Box>
            <T
              variant="body2"
              sx={{
                pl: 0.25,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: "2",
              }}
            >
              Search {searchDest}
            </T>
          </StyledMenuItem>
        );
      }}
      ListboxProps={{ sx: { py: 0 } }}
      // Stringify boolean flag value to avoid keys error
      getOptionLabel={(option) =>
        typeof option === "object" ? String((option as SearchOption).usePathPrefix) : ""
      }
      // Prevent dropdown from displaying if searching from root level
      PaperComponent={
        !currentPathLabel
          ? ({ children }) => <Box style={{ display: "none" }}>{children}</Box>
          : undefined
      }
    />
  );
};

export default ContextualSearch;
